import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { PhoneCTA, WhatsAppCTA } from "@/components/ContactButtons";
import { PHONE_DISPLAY_SHORT } from "@/lib/contact";
import { scrollToId } from "@/lib/track";

const LINKS = [
  { to: "/#service", label: "Le service", id: "service", testId: "nav-link-service" },
  { to: "/#simulateur", label: "Tarif", id: "simulateur", testId: "nav-link-tarif" },
  { to: "/#fonctionnement", label: "Fonctionnement", id: "fonctionnement", testId: "nav-link-fonctionnement" },
  { to: "/conseils", label: "Conseils", testId: "nav-link-conseils", page: true },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  const goAnchor = (id) => () => {
    if (location.pathname === "/") scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? "bg-[#f6f3ec]/90 backdrop-blur-md border-[#e5e1d8] shadow-[0_10px_40px_-24px_rgba(13,39,49,0.35)]"
          : "bg-[#f6f3ec]/60 backdrop-blur-sm border-transparent"
      }`}
    >
      <div className="container-avelis flex items-center justify-between h-[76px] gap-4">
        <Link to="/" data-testid="header-logo" aria-label="AVELIS — Retour à l'accueil" className="shrink-0">
          <img src="/avelis-logo.png" alt="AVELIS — Tarification infirmière" className="h-7 sm:h-8 w-auto" />
        </Link>

        <nav aria-label="Navigation principale" className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
          {LINKS.map((l) =>
            l.page ? (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `text-[13px] font-semibold tracking-wide transition-colors duration-200 ${
                    isActive ? "text-[#176b70]" : "text-[#0d2731]/75 hover:text-[#176b70]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                onClick={goAnchor(l.id)}
                className="text-[13px] font-semibold tracking-wide text-[#0d2731]/75 hover:text-[#176b70] transition-colors duration-200"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2.5">
          <span data-testid="header-phone-number" className="hidden 2xl:block text-[13px] font-extrabold tracking-wide text-[#0d2731] tabular-nums mr-1">
            {PHONE_DISPLAY_SHORT}
          </span>
          <PhoneCTA location="header" label="Appeler" className="hidden sm:inline-flex !px-5 !py-2.5 !text-[13px]" />
          <WhatsAppCTA location="header" label="WhatsApp" className="hidden sm:inline-flex !px-5 !py-2.5 !text-[13px]" />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Ouvrir le menu"
                className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-[#0d2731]/20 text-[#0d2731]"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82vw] max-w-xs bg-[#f6f3ec] border-l border-[#e5e1d8] p-0">
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="px-7 pt-7 pb-6 border-b border-[#e5e1d8]">
                  <img src="/avelis-logo.png" alt="AVELIS" className="h-7 w-auto" />
                </div>
                <nav aria-label="Navigation mobile" className="flex flex-col px-7 py-5 gap-1">
                  {LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`mobile-${l.testId}`}
                      onClick={l.page ? undefined : goAnchor(l.id)}
                      className={`py-3.5 text-lg font-bold tracking-tight border-b border-[#e5e1d8]/70 transition-colors ${
                        l.page && location.pathname === "/conseils" ? "text-[#176b70]" : "text-[#0d2731]"
                      }`}
                    >
                      {l.label}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto px-7 pb-8 flex flex-col gap-3">
                  <PhoneCTA location="header" label="Appeler AVELIS" className="w-full" />
                  <WhatsAppCTA location="header" className="w-full" />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
